// =============================================================================
// Empty State - By Share Only Model
// Displayed when a member/guest has no shared content
// =============================================================================

import React from 'react';
import { Lock, Copy, Share2, HelpCircle, Folder } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import type { EmptyStateReason } from '@/lib/empty-state-utils';

interface EmptyStateByShareOnlyProps {
  /** If true, shows the "Share a Space" CTA instead of "Request Access" */
  canManage?: boolean;
  /** Callback when user wants to share a space (only if canManage) */
  onShareSpace?: () => void;
  /** Variant: 'full' for main view, 'compact' for sidebar */
  variant?: 'full' | 'compact';
  /** Reason for empty state - determines displayed message */
  reason?: Exclude<EmptyStateReason, null>;
  /** User's org role - affects the displayed message */
  userRole?: 'member' | 'guest' | null;
  /** Additional className */
  className?: string;
}

const REQUEST_ACCESS_MESSAGE = 
  "Pourrais-tu me donner accès à un espace ou un projet sur Lapyno ? Je ne vois aucun contenu pour l'instant.";

const REQUEST_PROJECT_MESSAGE = 
  "J'ai accès à des espaces sur Lapyno, mais je ne vois aucun projet actif accessible. Pourrais-tu m'en partager un ?";

export function EmptyStateByShareOnly({
  canManage = false,
  onShareSpace,
  variant = 'full',
  reason = 'no-content',
  userRole = null,
  className,
}: EmptyStateByShareOnlyProps) {
  const message = reason === 'spaces-but-no-projects' 
    ? REQUEST_PROJECT_MESSAGE 
    : REQUEST_ACCESS_MESSAGE;

  const handleCopyRequest = async () => {
    try {
      await navigator.clipboard.writeText(message);
      toast.success('Message copié !', {
        description: 'Envoyez ce message à un administrateur de votre workspace.',
      });
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = message;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('Message copié !');
    }
  };

  // Compact variant for sidebar
  if (variant === 'compact') {
    const compactMessage = reason === 'spaces-but-no-projects' 
      ? 'Aucun projet accessible' 
      : 'Aucun contenu partagé';
    const Icon = reason === 'spaces-but-no-projects' ? Folder : Lock;

    return (
      <div className={cn("px-3 py-4 text-center", className)}>
        <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-muted flex items-center justify-center">
          <Icon className="h-5 w-5 text-muted-foreground" />
        </div>
        <p className="text-xs text-muted-foreground mb-2">
          {compactMessage}
        </p>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs gap-1"
              onClick={canManage ? onShareSpace : handleCopyRequest}
            >
              {canManage ? (
                <>
                  <Share2 className="h-3 w-3" />
                  Partager
                </>
              ) : (
                <>
                  <Copy className="h-3 w-3" />
                  Demander accès
                </>
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right" className="max-w-[200px]">
            {canManage 
              ? "Partagez un espace pour donner accès à tous ses projets"
              : "Copie un message pour demander l'accès à un Admin"
            }
          </TooltipContent>
        </Tooltip>
      </div>
    );
  }

  // Full variant for main view
  // UX: Empty state pédagogique post-invitation Organisation
  const title = reason === 'spaces-but-no-projects'
    ? 'Espaces partagés mais aucun projet accessible'
    : userRole === 'guest'
      ? 'Invité'
      : 'Bienvenue dans votre workspace';

  const description = reason === 'spaces-but-no-projects'
    ? 'Vous avez accès à des espaces, mais aucun projet actif ne vous est accessible. Demandez à un administrateur de vous partager un projet.'
    : userRole === 'guest'
      ? 'Vous êtes un invité. Un administrateur doit vous partager un espace ou un projet pour que vous puissiez y accéder.'
      : 'Vous avez accès au workspace. Les espaces privés doivent être partagés par un administrateur.';

  const MainIcon = reason === 'spaces-but-no-projects' ? Folder : Lock;

  return (
    <Card className={cn("max-w-md mx-auto", className)}>
      <CardHeader className="text-center pb-2">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
          <MainIcon className="h-8 w-8 text-muted-foreground" />
        </div>
        <CardTitle className="text-xl">{title}</CardTitle>
        <CardDescription className="mt-2">
          {description}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Help box */}
        <div className="flex items-start gap-3 p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 text-sm">
          <HelpCircle className="h-5 w-5 text-blue-500 shrink-0 mt-0.5" />
          <div className="text-blue-700 dark:text-blue-300">
            <p className="font-medium mb-1">Comment fonctionne le partage ?</p>
            <ul className="space-y-1 text-xs list-disc list-inside">
              <li>Un <strong>partage d'Espace</strong> donne accès à tous ses projets</li>
              <li>Un <strong>partage de Projet</strong> donne accès uniquement à ce projet</li>
            </ul>
          </div>
        </div>

        {/* CTA */}
        <div className="flex justify-center pt-2">
          {canManage ? (
            <Button onClick={onShareSpace} className="gap-2">
              <Share2 className="h-4 w-4" />
              Partager un espace
            </Button>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button onClick={handleCopyRequest} variant="outline" className="gap-2">
                  <Copy className="h-4 w-4" />
                  Demander un accès
                </Button>
              </TooltipTrigger>
              <TooltipContent className="max-w-[250px] text-center">
                <p className="text-xs">
                  Copie ce message dans ton presse-papier pour l'envoyer à un Admin
                </p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>

        {/* Copy preview for non-managers */}
        {!canManage && (
          <div className="mt-3 p-2 rounded border bg-muted/30 text-xs text-muted-foreground italic text-center">
            "{message}"
          </div>
        )}
      </CardContent>
    </Card>
  );
}
